<template>
  <md-card class="webhooksComponent">
    <md-card-header class="md-card-header-icon md-card-header-primary">
      <div class="card-icon">
        <md-icon>widgets</md-icon>
      </div>
      <md-toolbar class="fm-card-header">
        <h4 class="md-title">Webhooks</h4>
        <help-link :link="helpLink.link" title="webhooks"></help-link>
        <md-button
          class="md-simple"
          @click.native="handleNew(true)"
          data-tour="webhook_new"
        >
          <md-icon>add_circle</md-icon>
          New
        </md-button>
      </md-toolbar>
    </md-card-header>
    <md-card-content data-tour="application_card_webhooks">
      <loading-spinner
        v-if="loadingWebhooks"
        title="Webhooks"
        v-bind:loadingError="loadingError"
      ></loading-spinner>
      <md-table
        v-model="hooks"
        v-if="!loadingWebhooks"
        class="table-striped table-hover"
        :key="hooksKey"
      >
        <md-table-empty-state
          md-label="No Webhooks Found"
          md-description="It looks like you have no Webhooks. Go ahead and create one."
        >
          <md-button
            class="md-primary md-raised"
            @click="handleNew()"
            data-tour="webhook_create"
          >
            <md-icon>add_circle</md-icon>
            Create
          </md-button>
        </md-table-empty-state>
        <md-table-row
          slot="md-table-row"
          slot-scope="{ item }"
          :key="item.id"
          class="webhooksListingRow"
          @click="handleEdit(item)"
        >
          <md-table-cell
            md-label="Name"
            md-sort-by="name"
            :data-tour="firstInList(item.id) ? 'webhook_name' : false"
            >{{ item.name }}
          </md-table-cell>
          <md-table-cell
            md-label="URL"
            :data-tour="firstInList(item.id) ? 'webhook_url' : false"
            >{{ item.url }}
          </md-table-cell>
          <md-table-cell>
            <div
              class="md-layout-item md-size-50 md-small-size-100 advancedSettings"
            >
              <div v-if="item.batch">Batch</div>
              <div v-if="item.batch && item.disabled">,&nbsp;</div>
              <div v-if="item.disabled">Disabled</div>
              <div
                v-if="
                  item.events.some(event => event.includes('recording')) &&
                    !features.recording &&
                    !loadingFeatures
                "
              >
                <span v-if="item.batch || item.disabled">,&nbsp;</span>
                (Missing features, some events disabled)
              </div>
            </div>
          </md-table-cell>
          <md-table-cell md-label="Actions" class="actions webhook actions">
            <!-- <md-button
              class="md-just-icon md-info md-simple"
              @click.native.stop="handleEdit(item)"
              :data-tour="firstInList(item.id) ? 'webhook_edit' : false"
            >
              <md-icon>edit</md-icon>
            </md-button> -->
            <md-button
              class="md-just-icon md-danger md-simple"
              @click.native.stop="handleDelete(item)"
            >
              <md-icon>close</md-icon>
            </md-button>
          </md-table-cell>
        </md-table-row>
      </md-table>
    </md-card-content>
    <callback-url></callback-url>
    <webhook-modal
      v-if="localWebhookModalVisible"
      :localWebhook="localWebhookForModal"
      :context="context"
      @close="localWebhookModalClose"
      @autoSave="localWebhookModalClose"
    ></webhook-modal>
  </md-card>
</template>
<script>
import { get } from "vuex-pathify";
import swal from "sweetalert2";
import _ from "lodash";
import CallbackUrl from "./CallbackUrl";
import WebhookModel from "@/models/webhook";
import { isFirstInList } from "../../utils/global";
import { HelpLinkTemplate, LoadingSpinner } from "@/fmComponents";
import helpLinks from "../../contextualHelpLinks.js";
import WebhookModal from "@/fmComponents/webhooks/WebhookModal";
import { dataTourActive } from "../../utils/appcues";

export default {
  components: {
    CallbackUrl,
    LoadingSpinner,
    "help-link": HelpLinkTemplate,
    WebhookModal
  },
  data: () => ({
    hooksKey: 0, // a mechanism to cause Vue to re-render the webhooks table programmatically when necessary.
    loadingWebhooksMain: true,
    loadingWebhooksExt: true,
    loadingError: false,
    localWebhookModalVisible: false,
    localWebhookForModal: {},
    features: {
      rtmp: false,
      recording: false
    },
    loadingFeatures: true
  }),
  props: {
    appId: null,
    channelId: null,
    defaultChannelId: null
  },
  watch: {
    defaultChannelId: function(newDefaultChannelId, oldVal) {
      // this updates from the parent after the mounted(), so it needs to be a watcher
      if (newDefaultChannelId) {
        this.loadEntitiesWebhooksExt(newDefaultChannelId);
      }
    }
  },
  computed: {
    appChannels: get("channels/entities"),
    loadingWebhooks: function() {
      return (
        (this.defaultChannelId ? this.loadingWebhooksExt : false) ||
        this.loadingWebhooksMain
      );
    },
    hooksMain: get("webhooks/entities") || [], // Per mounted() below: If loaded from Application.vue this will contain Application hooks.  If loaded from channel.vue it will contain Channel hooks.
    hooksExt: get("webhooksExt/entities") || [], // Per the defaultChannelId watcher above this will load the default channel's hooks
    hooks: function() {
      // Application level needs both app and default channel hooks - defaultChannelId's presense indicates this component is being called from Applicaiton.vue
      if (this.defaultChannelId) {
        let nothing = this.hooksKey; // Necessary to force re-compute, otherwise Vue doesn't update the final "dirty" change that removes the SubmitSnackbar
        return _.sortBy(_.concat(this.hooksMain, this.hooksExt), [
          function(r) {
            return r.name;
          }
        ]); // concat & sort by name
      } else {
        return _.sortBy(this.hooksMain, [
          function(r) {
            return r.name;
          }
        ]);
      }
    },
    ogHooksMain: get("webhooks/ogEntities") || [], // see hooksMain comment above
    ogHooksExt: get("webhooksExt/ogEntities") || [], // see hooksExt comment above
    ogHooks: function() {
      // Application level needs both app and default channel hooks - defaultChannelId's presense indicates this component is being called from Applicaiton.vue
      if (this.defaultChannelId) {
        let nothing = this.hooksKey; // Necessary to force re-compute, otherwise Vue doesn't update the final "dirty" change that removes the SubmitSnackbar
        return _.sortBy(_.concat(this.ogHooksMain, this.ogHooksExt), [
          function(r) {
            return r.name;
          }
        ]); // concat & sort by name
      } else {
        return _.sortBy(this.ogHooksMain, [
          function(r) {
            return r.name;
          }
        ]);
      }
    },
    context: function() {
      return this.channelId ? "channel" : "app";
    },
    helpLink: function() {
      return this.channelId
        ? helpLinks.channelConfig.webhookConfig
        : helpLinks.applicationConfig.webhookConfig;
    },
  },
  mounted() {
    var configType = this.channelId ? "channelConfigId" : "applicationConfigId";
    var id = this.channelId ? this.channelId : this.appId;
    this.loadEntitiesWebhooksMain(configType, id);
    // Default channel loads after application info has been loaded in parent (see watcher on defaultChannelId above )
    // -- If the user navigates away from this page and comes back the call below is needed to re-fire the loading sequence
    if (this.defaultChannelId) {
      this.loadEntitiesWebhooksExt(this.defaultChannelId);
    }
    this.$store
      .dispatch("subscription/loadSubscription", localStorage.subscriptionId)
      .then((result) => {
        // Populate a local enabled feature map
        result.enabledFeatures.map(feature => {
          this.features[feature] = true;
          this.loadingFeatures = false;
        });
      });
  },
  methods: {
    firstInList(id) {
      return isFirstInList(id, "webhooks", this.hooks);
    },
    dirty: function(forceReload, autoSave) {
      if (forceReload) {
        this.bumpKey(); // `forceReload` flag causes UI rerender.
      }
      if (autoSave) {
        this.$emit("hooksAutoSave", !_.isEqual(this.hooks, this.ogHooks));
      } else {
        this.$emit("dirtyHooks", !_.isEqual(this.hooks, this.ogHooks));
      }
    },
    bumpKey: function() {
      /*
        Note: this is important and took a while to figure out.

        Vue does not update the webhook table UI even when its bound data changes.
        Well, it does the first time, but then if the object in the collection changes
        due to it being updated, or rolled back, in the store Vue no longer notices the
        data changing and so does not update the UI.

        *Important* - the data is still bound! Making changes through the UI works as expected.
        If you type, or check a checkbox, then inspect the store the bound data is what it should be.
        But the UI is not updating, so you cannot see that you checked the checkbox ... Vue does
        not know the data has changed, but it has.

        I found a workaround. I place a `:key` on the table. When you increment this key it
        triggers a rerender so that the UI reflects the state of the bound data.
      */
      this.hooksKey++;
    },
    updateConfig: function() {
      var me = this;

      var dirtyHooks = _.differenceWith(this.hooks, this.ogHooks, _.isEqual);
      if (dirtyHooks && dirtyHooks.length) {
        var doFinally = function(completed) {
          if (completed === dirtyHooks.length) {
            /*
             Recalculate (and emit) dirty value to parent to clear the submit snackbar.

             Also, the underlying bound data in the store has been updated (or rolled
             back on error), so we pass `true` to force UI rerender.
            */
            me.dirty(true, false);
          }
        };

        var completed = 0;
        dirtyHooks.forEach(hook => {
          // if the hook is marked for transfer
          if (hook.transfer) {
            // delete the current hook
            this.$store
              .dispatch(hook.transfer.from + "/deleteEntity", hook.id)
              .then(() => {
                // set up a new hook
                let newHook = new WebhookModel(
                  JSON.parse(JSON.stringify(hook))
                ); // decouple from reactive/global and create a new one
                // Set the parent relation
                // if tranferring to webhooks
                if (hook.transfer.to === "webhooks") {
                  newHook.applicationConfigId = this.appId; // webhooks contains application hooks
                }
                // otherwise its a default channel hook in webhooksExt
                else {
                  newHook.channelConfigId = this.defaultChannelId; // webhooksExt contains (default) channel hooks
                }
                // create the new hook
                this.$store
                  .dispatch(hook.transfer.to + "/createEntity", newHook)
                  .then(() => {
                    doFinally(++completed);
                  })
                  .catch(() => {
                    doFinally(++completed);
                  });
              });
          } else {
            // Since hooks derrives from both the state webhooks and webhooksExt, determine which webhook type we are in
            let stateLocation = this.hooksMain.find(r => r.id == hook.id)
              ? "webhooks"
              : "webhooksExt";
            // Dispatch the hook for update
            this.$store
              .dispatch(stateLocation + "/updateEntity", hook)
              .then(() => {
                doFinally(++completed);
              })
              .catch(() => {
                doFinally(++completed);
              });
          }
        });
      }
    },
    localWebhookModalOpen(item) {
      if (item === "new") {
        this.localWebhookForModal = new WebhookModel();
      } else {
        this.localWebhookForModal = JSON.parse(JSON.stringify(item)); // decouple from reactivity
      }
      this.$emit("updateHideSubmitSnackbar", "hidden");
      this.localWebhookModalVisible = true;
    },
    localWebhookModalClose(webhook, autoSave) {
      // webhook is not tied to global/vuex
      this.$emit("updateHideSubmitSnackbar", "visible");
      if (!autoSave) {
        this.localWebhookModalVisible = false;
      }
      let hookEventType = webhook.eventType; // capture the event type
      delete webhook.eventType; // remove the event type for submission to API (or saving to global/vuex)

      // new webhook - webhook exists && there's no id yet
      if (webhook && !webhook.id) {
        // We are in Channel.vue
        if (this.context === "channel") {
          // set the webhook configId for the current channel
          webhook.channelConfigId = this.channelId;
          this.$store.dispatch("webhooks/createEntity", webhook);
        }
        // We are in Application.vue
        else {
          if (hookEventType == "client") {
            // set the webhook applictionconfig id
            webhook.applicationConfigId = this.appId;
            this.$store.dispatch("webhooks/createEntity", webhook);
          } else {
            // this is a default channel
            // set the default channel hook & create
            webhook.channelConfigId = this.defaultChannelId;
            this.$store.dispatch("webhooksExt/createEntity", webhook); // default channel webhooks are stored in webhooksExt
          }
        }
      }
      // Editing a webhook
      if (webhook && webhook.id) {
        let webhookIsInHooksMain = this.hooksMain.find(r => r.id == webhook.id)
          ? true
          : false;
        let webhookIsInHooksExt = this.hooksExt.find(r => r.id == webhook.id)
          ? true
          : false; // should be equivalent to !webhookIsInHooksMain but done for sanity check and clarity of code below

        // if its a channel hook on the channel page (it can't become an application hook)
        if (webhookIsInHooksMain && this.context === "channel") {
          // webhookIsInHooksMain is a sanity check
          // find and replace the old entity with the new one
          this.$store.commit("webhooks/replaceEntity", webhook);
          this.dirty(false, autoSave);
        }
        // if the hook was an appliction hook & stayed an application hook
        else if (
          webhookIsInHooksMain &&
          this.context === "app" &&
          hookEventType === "client"
        ) {
          // find and replace the old entity with the new one
          this.$store.commit("webhooks/replaceEntity", webhook);
          this.dirty(false, autoSave);
        }
        // if the hook changed from application to default channel
        else if (
          webhookIsInHooksMain &&
          this.context === "app" &&
          hookEventType !== "client"
        ) {
          // mark the hook as needing to be transferred
          webhook.transfer = { from: "webhooks", to: "webhooksExt" };
          this.$store.commit("webhooks/replaceEntity", webhook);
          this.dirty(false, autoSave);
        }
        // if the hook was a default hook & stayed a default hook
        else if (
          webhookIsInHooksExt &&
          this.context === "app" &&
          hookEventType !== "client"
        ) {
          // find and replace the old entity with the new one
          this.$store.commit("webhooksExt/replaceEntity", webhook);
          this.dirty(false, autoSave);
        }
        // if the hook changed from default channel to application
        else if (
          webhookIsInHooksExt &&
          this.context === "app" &&
          hookEventType === "client"
        ) {
          // mark the hook as needing to be transferred
          webhook.transfer = { from: "webhooksExt", to: "webhooks" };
          this.$store.commit("webhooksExt/replaceEntity", webhook);
          this.dirty(false, autoSave);
        }
      }
      if (!webhook) {
        // closing without saving
        for (let i = 0; i < this.hooks.length; i++) {
          if (this.hooks[i].id === this.localWebhookForModal.id) {
            this.hooks[i].url = this.localWebhookForModal.url;
          }
        }
      }
    },
    handleNew(topRightCreateButton = false) {
      if (!dataTourActive || topRightCreateButton) {
        this.localWebhookModalOpen("new");
      }
    },
    handleEdit(item) {
      this.localWebhookModalOpen(item);
    },
    handleDelete(item) {
      swal
        .fire({
          title: "Are you sure?",
          text: `You won't be able to revert this!`,
          type: "warning",
          showCancelButton: true,
          confirmButtonClass: "md-button md-success btn-fill",
          cancelButtonClass: "md-button md-danger btn-fill",
          confirmButtonText: "Yes, delete it!",
          buttonsStyling: false
        })
        .then(result => {
          if (result.value) {
            let stateLocation = this.hooksMain.find(r => r.id == item.id)
              ? "webhooks"
              : "webhooksExt";
            this.$store
              .dispatch(stateLocation + "/deleteEntity", item.id)
              .then(() => {
                swal.fire({
                  title: "Deleted!",
                  text: `You deleted ${item.name}`,
                  type: "success",
                  confirmButtonClass: "md-button md-success btn-fill",
                  buttonsStyling: false
                });
              });
          }
        });
    },
    getCallbackUrlHtml: function() {
      var html = document
        .getElementById("triggersCallbackHtml")
        .cloneNode(true);
      html.removeAttribute("id");
      html.classList.remove("hidden");
      return html;
    },
    loadEntitiesWebhooksMain: function(configType, id) {
      this.$store
        .dispatch("webhooks/loadEntities", [{ k: configType, v: id }])
        .then(() => {
          this.loadingWebhooksMain = false;
        })
        .catch(ex => {
          this.loadingError = true;
        });
    },
    loadEntitiesWebhooksExt: function(defaultChannelId) {
      this.$store
        .dispatch("webhooksExt/loadEntities", [
          { k: "channelConfigId", v: defaultChannelId }
        ])
        .then(() => {
          this.loadingWebhooksExt = false;
        })
        .catch(ex => {
          this.loadingError = true;
        });
    }
  }
};
</script>
<style lang="scss">
.md-table-row.webhooksListingRow {
  cursor: pointer;
  .md-input {
    cursor: pointer;
  }
}
.md-card.webhooksComponent {
  z-index: 2;
}
.advancedSettings {
  display: flex;
  flex-direction: row;
  align-items: center;
}
.md-table-cell.actions {
  width: 80px;
  white-space: nowrap;
  button {
    cursor: pointer;
  }
}
.fm-card-header {
  width: auto;
  background-color: inherit !important;
  box-shadow: none;
  .md-title {
    color: #3c4858 !important;
    flex: 1;
  }
}

#triggersHtml {
  display: none;
}
#triggersCallbackHtml {
  display: none;
}
.swal2-popup #swal2-content .fm-triggers {
  text-align: left;
  .swal2-checkbox {
    justify-content: left;
  }
  .swal2-label {
    font-size: 0.8em;
    font-weight: normal;
  }
  h4 {
    font-size: 1.3rem;
  }
}

.no-display {
  display: none;
}
</style>
